import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Stethoscope, Play, Copy, ChevronDown, ChevronRight, Loader2, KeyRound } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import CheckRow from './diagnostics/CheckRow';
import SendTestCard from './diagnostics/SendTestCard';
import { StatusIcon, STATUS, buildReportText } from './diagnostics/statusUi';

const VERDICT = {
  healthy: { status: 'pass', title: 'Everything checked looks healthy', sub: 'No problems were found with your WhatsApp / Meta connection.' },
  attention: { status: 'warn', title: 'Working, but some things need attention', sub: 'Nothing is blocking sends right now, but the items below can cause problems later.' },
  critical: { status: 'fail', title: 'Problems found that can stop WhatsApp from working', sub: 'Fix the failed items first — they are listed below with the exact steps.' },
};

const EMPTY_INPUTS = { appId: '', businessId: '', appSecret: '', version: 'v25.0', templateName: '', templateLanguage: 'en_US' };

const inputCls = 'w-full px-3 h-9 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px] text-[#080707] placeholder:text-[#9A9894] focus:bg-white focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none transition-all';

function Field({ label, hint, children }) {
  return (
    <div>
      {/* The hint stays outside the <label>: the app's global label style uppercases everything inside it. */}
      <label className="block space-y-1">
        <span className="block text-[11px] font-bold text-[#706E6B]">{label}</span>
        {children}
      </label>
      {hint && <p className="text-[11px] text-[#706E6B] mt-1">{hint}</p>}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 text-[12px]">
      <span className="text-[#706E6B] shrink-0">{label}</span>
      <span className="text-[#080707] font-semibold text-right break-all">{value || '—'}</span>
    </div>
  );
}

export default function MetaDiagnosticsPage() {
  const isAdmin = useSelector((s) => s.auth?.isAdmin);
  const [config, setConfig] = useState(null);
  const [inputs, setInputs] = useState(EMPTY_INPUTS);
  const [remember, setRemember] = useState(true);
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState('all');
  const [openGroups, setOpenGroups] = useState(new Set());
  const [actionBusy, setActionBusy] = useState(null);

  const setInput = (k) => (e) => setInputs((p) => ({ ...p, [k]: e.target.value }));

  const run = useCallback(async (override) => {
    setRunning(true);
    try {
      const src = override || inputs;
      const typed = Object.fromEntries(Object.entries(src).filter(([, v]) => typeof v === 'string' && v.trim()));
      const res = await api.post('/meta/diagnostics/run', {
        inputs: { ...typed, callbackBase: window.location.origin },
        remember: !!remember,
      });
      setReport(res.data);
      setOpenGroups(new Set(res.data.groups.filter((g) => g.status === 'fail' || g.status === 'warn').map((g) => g.id)));
    } catch (err) {
      alertService.toast('error', err.response?.data?.error || 'Could not run diagnostics');
    } finally {
      setRunning(false);
    }
  }, [inputs, remember]);

  // Load what the CRM already knows, prefill the stored extra IDs, then run once so the page answers on arrival.
  useEffect(() => {
    let alive = true;
    (async () => {
      let next = EMPTY_INPUTS;
      try {
        const res = await api.get('/meta/diagnostics/config', { params: { callbackBase: window.location.origin } });
        if (!alive) return;
        setConfig(res.data);
        next = { ...EMPTY_INPUTS, appId: res.data.stored?.appId || '', businessId: res.data.stored?.businessId || '' };
        setInputs(next);
      } catch (err) {
        if (alive) alertService.toast('error', err.response?.data?.error || 'Could not load the WhatsApp connection');
      }
      if (alive) run(next);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doAction = async (action) => {
    const spec = {
      subscribe_app: { url: '/meta/diagnostics/actions/subscribe-app', title: 'Subscribe this app to your WhatsApp account?', text: 'This changes the webhook subscription on Meta so inbound messages and delivery receipts are sent to this app.', ok: 'Subscribe' },
      reactivate: { url: '/meta/diagnostics/actions/reactivate', title: 'Reactivate the connection?', text: 'The CRM will re-check your token and phone number with Meta first, and only then unblock sending.', ok: 'Reactivate' },
    }[action];
    if (!spec) return;
    if (!(await alertService.confirm(spec.title, spec.text, 'question', spec.ok))) return;
    setActionBusy(action);
    try {
      const res = await api.post(spec.url);
      if (res.data.ok === false) alertService.toast('error', `${res.data.message}${res.data.hint ? ` — ${res.data.hint}` : ''}`);
      else alertService.toast('success', res.data.message);
    } catch (err) {
      const d = err.response?.data;
      alertService.toast('error', d ? `${d.error}${d.hint ? ` — ${d.hint}` : ''}` : 'Action failed');
    } finally {
      setActionBusy(null);
    }
    // The throttle protects Meta from rapid re-runs; give it a moment before refreshing.
    setTimeout(() => run(), 3200);
  };

  const copyReport = async () => {
    const text = buildReportText(report);
    try {
      await navigator.clipboard.writeText(text);
      alertService.toast('success', 'Report copied — safe to share, it contains no secrets');
    } catch {
      alertService.toast('error', 'Could not copy to the clipboard');
    }
  };

  const toggleGroup = (id) => setOpenGroups((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const visibleGroups = useMemo(() => {
    if (!report) return [];
    if (filter === 'all') return report.groups;
    return report.groups
      .map((g) => ({ ...g, checks: g.checks.filter((c) => c.status === 'fail' || c.status === 'warn') }))
      .filter((g) => g.checks.length);
  }, [report, filter]);

  const verdict = report ? VERDICT[report.summary.verdict] : null;
  const connected = !!config?.configured;

  return (
    <DashboardModuleShell
      title="WhatsApp Diagnostics"
      subtitle="Checks your CRM settings and your Meta account live, and tells you exactly what is wrong and how to fix it."
      icon={Stethoscope}
      actions={(
        <>
          <Button variant="secondary" onClick={copyReport} disabled={!report} icon={Copy}>Copy report</Button>
          <Button onClick={() => run()} loading={running} icon={Play}>{report ? 'Run again' : 'Run diagnostics'}</Button>
        </>
      )}
    >
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
        <div className="xl:col-span-2 space-y-6">
          {/* Verdict */}
          {!report && running && (
            <div className="sf-card"><div className="sf-card-body flex items-center gap-3 text-[13px] text-[#706E6B]"><Loader2 className="animate-spin" size={18} />Running checks against Meta and your CRM…</div></div>
          )}
          {verdict && (
            <div className={cn('rounded-md border p-4 sm:p-5 flex items-start gap-3', STATUS[verdict.status].soft, running && 'opacity-60')}>
              <StatusIcon status={verdict.status} size={26} />
              <div className="min-w-0">
                <p className="text-[15px] font-bold text-[#080707]">{verdict.title}</p>
                <p className="text-[12px] text-[#080707]/80 mt-0.5">{verdict.sub}</p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="sf-badge sf-badge-success">{report.summary.pass} passed</span>
                  <span className="sf-badge sf-badge-warning">{report.summary.warn} warning{report.summary.warn === 1 ? '' : 's'}</span>
                  <span className="sf-badge sf-badge-error">{report.summary.fail} failed</span>
                  {report.summary.skip > 0 && <span className="sf-badge sf-badge-neutral">{report.summary.skip} skipped</span>}
                  <span className="text-[11px] text-[#706E6B]">{report.summary.total} checks · {(report.durationMs / 1000).toFixed(1)}s</span>
                </div>
                {report.context && (
                  <p className="text-[11px] text-[#706E6B] mt-2 break-all">
                    Phone Number ID {report.context.phoneNumberId} · WABA {report.context.wabaId} · App {report.context.appId || 'unknown'} · token: {report.context.messagingToken} · {report.context.mode === 'full' ? 'full check' : 'limited check (add the App Secret for a deeper one)'} · Graph {report.apiVersion}
                  </p>
                )}
                {report.inputWarnings?.map((w) => <p key={w} className="text-[11px] text-[#8C4B02] mt-1">{w}</p>)}
              </div>
            </div>
          )}

          {/* Top issues */}
          {report && report.issues.length > 0 && (
            <div className="sf-card">
              <div className="sf-card-header"><h3 className="sf-card-title">What to fix first ({report.issues.length})</h3></div>
              <div className="divide-y divide-[#D8DDE6]">
                {report.issues.map((issue) => {
                  const check = report.groups.flatMap((g) => g.checks).find((c) => c.id === issue.id);
                  return (
                    <div key={issue.id}>
                      <CheckRow check={check} canReactivate={report.canReactivate} isAdmin={isAdmin} actionBusy={actionBusy} onAction={doAction} defaultOpen={false} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Inputs */}
        <div className="space-y-6">
          <div className="sf-card">
            <div className="sf-card-header"><h3 className="sf-card-title flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#0176D3]" />Connection being tested</h3></div>
            <div className="sf-card-body space-y-2">
              {config ? (connected ? (
                <>
                  <Row label="Status in CRM" value={config.status} />
                  <Row label="Phone Number ID" value={config.phoneNumberId} />
                  <Row label="WABA ID" value={config.wabaId} />
                  <Row label="Access token" value={config.accessToken} />
                  <Row label="Flows token" value={config.flowsToken || 'Not set'} />
                  <Row label="App Secret" value={config.appSecretConfigured ? 'Saved' : 'Not saved'} />
                  <p className="text-[11px] text-[#706E6B] pt-1">These come from API Configuration. Tokens are never shown or sent to your browser.</p>
                </>
              ) : (
                <p className="text-[12px] text-[#8C4B02]">WhatsApp isn't connected yet. Add your credentials in the API Configuration tab.</p>
              )) : <p className="text-[12px] text-[#706E6B]">Loading…</p>}
            </div>
          </div>

          <div className="sf-card">
            <div className="sf-card-header"><h3 className="sf-card-title">Extra details (optional)</h3></div>
            <div className="sf-card-body space-y-4">
              <p className="text-[11px] text-[#706E6B]">Only things the CRM doesn't already store. Leave blank to skip the checks that need them.</p>
              <Field label="Meta App ID" hint="Detected from your token automatically. Enter it to cross-check the token belongs to this app.">
                <input className={inputCls} value={inputs.appId} onChange={setInput('appId')} inputMode="numeric" placeholder="e.g. 2137553653638935" />
              </Field>
              <Field label="Business Portfolio ID" hint="Business Settings → Business info. Used to confirm your WABA belongs to it.">
                <input className={inputCls} value={inputs.businessId} onChange={setInput('businessId')} inputMode="numeric" placeholder="e.g. 1055…" />
              </Field>
              <Field label="App Secret" hint={config?.appSecretConfigured ? 'Already saved. Type one only to override it for this run — it is not stored.' : 'Not saved in the CRM. Enter it to unlock the app-level and webhook checks (used for this run only, not stored).'}>
                <input className={inputCls} type="password" autoComplete="off" value={inputs.appSecret} onChange={setInput('appSecret')} placeholder={config?.appSecretConfigured ? '••••••••••••' : 'From Meta App → Settings → Basic'} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Template to verify">
                  <input className={inputCls} value={inputs.templateName} onChange={setInput('templateName')} placeholder="order_confirmation" />
                </Field>
                <Field label="Language">
                  <input className={inputCls} value={inputs.templateLanguage} onChange={setInput('templateLanguage')} placeholder="en_US" />
                </Field>
              </div>
              <Field label="Graph API version">
                <input className={inputCls} value={inputs.version} onChange={setInput('version')} placeholder="v25.0" />
              </Field>
              {isAdmin && (
                <label className="flex items-center gap-2 text-[12px] text-[#080707] normal-case font-normal">
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                  Remember the App ID and Business ID for next time
                </label>
              )}
              <Button onClick={() => run()} loading={running} icon={Play} className="w-full">Run with these details</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Groups */}
      {report && (
        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-[15px] font-bold text-[#080707]">All checks</h3>
            <div className="inline-flex rounded border border-[#D8DDE6] overflow-hidden text-[12px] font-semibold" role="group" aria-label="Filter checks">
              {[['all', 'Everything'], ['issues', 'Problems only']].map(([k, label]) => (
                <button key={k} type="button" onClick={() => setFilter(k)}
                  className={cn('px-3 h-8', filter === k ? 'bg-[#0176D3] text-white' : 'bg-white text-[#706E6B] hover:bg-[#F3F2F2]')}>{label}</button>
              ))}
            </div>
          </div>

          {visibleGroups.length === 0 && <div className="sf-card"><div className="sf-card-body text-[13px] text-[#706E6B]">No problems to show.</div></div>}

          {visibleGroups.map((g) => {
            const open = openGroups.has(g.id);
            const fails = g.checks.filter((c) => c.status === 'fail').length;
            const warns = g.checks.filter((c) => c.status === 'warn').length;
            return (
              <div key={g.id} className={cn('sf-card', running && 'opacity-60')}>
                <button type="button" className="sf-card-header w-full flex items-center justify-between gap-3 text-left" onClick={() => toggleGroup(g.id)} aria-expanded={open}>
                  <span className="flex items-center gap-3 min-w-0">
                    <StatusIcon status={g.status} />
                    <span className="min-w-0">
                      <span className="block sf-card-title">{g.title}</span>
                      <span className="block text-[11px] text-[#706E6B] font-normal truncate">{g.description}</span>
                    </span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    {fails > 0 && <span className="sf-badge sf-badge-error">{fails} failed</span>}
                    {warns > 0 && <span className="sf-badge sf-badge-warning">{warns} warning{warns > 1 ? 's' : ''}</span>}
                    {!fails && !warns && <span className="sf-badge sf-badge-success">OK</span>}
                    {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </span>
                </button>
                {open && (
                  <div className="divide-y divide-[#D8DDE6]">
                    {g.checks.map((c) => (
                      <CheckRow key={c.id} check={c} defaultOpen={c.status === 'fail'} canReactivate={report.canReactivate} isAdmin={isAdmin} actionBusy={actionBusy} onAction={doAction} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <SendTestCard isAdmin={!!isAdmin} connected={connected} />
    </DashboardModuleShell>
  );
}
